import type { CSSProperties, ReactElement } from 'react';

import { whiteAlpha } from '@/config/appColors';
import { BlurAnimation } from '@/presentation/widgets/BlurAnimation';
import { ShimmerCard } from '@/presentation/widgets/ShimmerCard';
import { lightenColor } from '@/utils/extensions/colorExtension';
import { LoremIpsum } from '@/utils/loremIpsum';

import styles from './PersonQuestionCard.module.css';

/**
 * Перенос `chat_page/person_questions_grid/person_question_card.dart`.
 *
 * В Dart было два именованных конструктора — `.loading()` и `.data()`.
 * Здесь один компонент с флагом [PersonQuestionCardProps.isShimmer]:
 * `.loading()` отличался только заглушкой вместо текста и включённым
 * мерцанием, отдельный компонент ради этого не нужен.
 */

/** `Colors.white.withValues(alpha: 0.2)` — и фон, и цвет рамки. */
const BACKGROUND_COLOR = whiteAlpha(0.2);

/**
 * `highlightColor: backgroundColor.lightenColor(0.9)`.
 *
 * Считается той же формулой, что в Dart, а не вписан готовым числом:
 * осветление белого с альфой 0.2 снова даёт белый с альфой 0.2, и это
 * видно только из самого вызова.
 */
const HIGHLIGHT_COLOR = lightenColor(BACKGROUND_COLOR, 0.9);

export interface PersonQuestionCardProps {
  /**
   * `PersonQuestionCard.data(question: question.question)`. У `.loading()`
   * текста нет — там стоит та же заглушка, что в Dart-конструкторе.
   */
  readonly question?: string;
  readonly onPressed?: (() => void) | null;
  /** `PersonQuestionCard.loading()` */
  readonly isShimmer?: boolean;
}

export function PersonQuestionCard(props: PersonQuestionCardProps): ReactElement {
  const { question = LoremIpsum.defaultTitle, onPressed, isShimmer = false } = props;
  const style = {
    '--card-background': BACKGROUND_COLOR,
    '--card-highlight': HIGHLIGHT_COLOR,
  } as CSSProperties;
  return (
    <div className={styles.clip} style={style}>
      <ShimmerCard enabled={isShimmer}>
        <div className={styles.blur}>
          <button
            type="button"
            className={styles.button}
            disabled={onPressed == null}
            onClick={() => onPressed?.()}
          >
            <div className={styles.card}>
              <BlurAnimation animate={isShimmer} className={styles.text}>
                {question}
              </BlurAnimation>
            </div>
          </button>
        </div>
      </ShimmerCard>
    </div>
  );
}
